import React, { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertTriangle, ShieldAlert, Trash2, History, Loader2, Wrench } from "lucide-react";
import { toast } from "sonner";

const CONFIRM_PHRASE = "DELETE ALL DEALS";

/**
 * Admin → Danger Zone.
 *
 * One-off destructive operations gated behind explicit confirmation phrases.
 * Currently: "Purge all deals" (used before a fresh CSV re-import).
 * Every purge writes an entry to admin_audit_log with actor + counts.
 */
export default function DangerZonePanel() {
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [resetIsLive, setResetIsLive] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, h] = await Promise.all([
        api.get("/admin/purge/deals/preview"),
        api.get("/admin/purge/deals/history"),
      ]);
      setPreview(p.data);
      setHistory(h.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Danger Zone");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const runPurge = async () => {
    if (confirmText !== CONFIRM_PHRASE) {
      toast.error(`You must type ${CONFIRM_PHRASE} exactly`);
      return;
    }
    setRunning(true);
    try {
      const r = await api.post("/admin/purge/deals", {
        confirmation: confirmText,
        reset_is_live: resetIsLive,
      });
      toast.success(
        `Purged ${r.data.deleted} deals${resetIsLive ? ` · reset ${r.data.accounts_reset_is_live} accounts` : ""}`
      );
      setDialogOpen(false);
      setConfirmText("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="danger-zone-panel">
      {/* Big warning banner */}
      <div className="rounded-lg border-2 border-rose-300 bg-rose-50 p-4 flex items-start gap-3">
        <ShieldAlert className="h-6 w-6 text-rose-600 shrink-0 mt-0.5" />
        <div>
          <div className="font-heading text-lg font-bold text-rose-900">Danger zone</div>
          <div className="text-sm text-rose-800 mt-1">
            Operations here are <b>destructive and irreversible</b>. Take a MongoDB Atlas snapshot before
            running any of them. Every action is written to the admin audit log with your email.
          </div>
        </div>
      </div>

      {/* Repair Forecasts card (safe / idempotent) */}
      <Card className="border-blue-200" data-testid="repair-forecasts-card">
        <CardHeader className="border-b border-blue-100 py-3 bg-blue-50/40">
          <CardTitle className="text-base font-heading flex items-center gap-2 text-blue-900">
            <Wrench className="h-4 w-4 text-blue-600" /> Repair deal forecasts
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-3">
          <p className="text-sm text-slate-700">
            Recomputes <code className="text-xs bg-slate-100 px-1 rounded">forecast_value</code>,{" "}
            <code className="text-xs bg-slate-100 px-1 rounded">forecast_gp</code>,{" "}
            <code className="text-xs bg-slate-100 px-1 rounded">mrr</code>, <code className="text-xs bg-slate-100 px-1 rounded">gp</code> and{" "}
            <code className="text-xs bg-slate-100 px-1 rounded">end_date</code> from
            <b> revenue × probability</b> for every deal. Use after a bulk import if
            weighted-forecast totals show £0. <b>Safe & idempotent</b> — probability values are preserved.
          </p>
          <RepairForecastsButton />
        </CardContent>
      </Card>

      {/* Purge Deals card */}
      <Card className="border-rose-200" data-testid="purge-deals-card">
        <CardHeader className="border-b border-rose-100 py-3 bg-rose-50/40">
          <CardTitle className="text-base font-heading flex items-center gap-2 text-rose-900">
            <Trash2 className="h-4 w-4 text-rose-600" /> Purge all deals
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          <p className="text-sm text-slate-700">
            Deletes every deal in the CRM (open pipeline + Closed Won/Lost). Optionally resets{" "}
            <code className="text-xs bg-slate-100 px-1 rounded">accounts.is_live</code> to{" "}
            <code className="text-xs bg-slate-100 px-1 rounded">false</code> so the customer KPI
            reflects the clean slate. Use before re-importing deals from a fresh CSV.
          </p>

          {loading && (
            <div className="text-xs text-slate-500 font-mono flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading preview…
            </div>
          )}

          {preview && (
            <div className="rounded border border-slate-200 bg-slate-50 p-4" data-testid="purge-preview">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-2">
                What will happen
              </div>
              <div className="grid sm:grid-cols-3 gap-3 text-sm">
                <PreviewStat label="Deals to delete" value={preview.total_deals} testid="preview-total-deals" />
                <PreviewStat
                  label="Accounts to flip to non-live"
                  value={preview.live_accounts_will_flip}
                  testid="preview-live-accounts"
                />
                <PreviewStat
                  label="Stages affected"
                  value={preview.by_stage.length}
                  testid="preview-stages"
                />
              </div>
              {preview.by_stage.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
                    Breakdown by stage
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {preview.by_stage.map((s) => (
                      <span
                        key={s.stage}
                        className="text-xs bg-white border border-slate-200 rounded-full px-2 py-0.5 font-mono"
                      >
                        {s.stage}: <b className="text-slate-900">{s.count}</b>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 pt-2">
            <Button
              variant="destructive"
              onClick={() => setDialogOpen(true)}
              disabled={loading || !preview || preview.total_deals === 0}
              data-testid="open-purge-dialog"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              Purge all deals…
            </Button>
            {preview?.total_deals === 0 && (
              <span className="text-xs text-slate-500 italic">No deals to delete.</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Audit history */}
      <Card className="border-slate-200" data-testid="purge-history-card">
        <CardHeader className="border-b border-slate-100 py-3">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <History className="h-4 w-4 text-slate-500" /> Purge history
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {history.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-6">
              No prior purges. Every purge you run will show up here for the audit trail.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-left">
                <tr>
                  <th className="px-4 py-2">When</th>
                  <th className="px-4 py-2">Actor</th>
                  <th className="px-4 py-2 text-right">Deals deleted</th>
                  <th className="px-4 py-2 text-right">Accounts flipped</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-b border-slate-100" data-testid={`purge-audit-${h.id}`}>
                    <td className="px-4 py-2 text-xs font-mono text-slate-500">{fmtDate(h.at)}</td>
                    <td className="px-4 py-2 text-xs">{h.actor_email}</td>
                    <td className="px-4 py-2 text-right font-mono text-sm font-semibold text-rose-700">
                      {h.deals_deleted}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-xs text-slate-500">
                      {h.accounts_is_live_reset}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Confirm dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!running) { setDialogOpen(o); if (!o) setConfirmText(""); } }}>
        <DialogContent className="max-w-md" data-testid="purge-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-900">
              <AlertTriangle className="h-5 w-5 text-rose-600" />
              Confirm deal purge
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-700 pt-2">
              This will <b className="text-rose-800">permanently delete {preview?.total_deals ?? 0} deals</b>
              {resetIsLive && preview ? <> and flip <b>{preview.live_accounts_will_flip}</b> accounts to non-live</> : null}.
              Type <code className="bg-slate-100 px-1 rounded text-xs font-mono">{CONFIRM_PHRASE}</code> below to proceed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="confirm-phrase" className="text-xs uppercase tracking-wider font-mono text-slate-600">
              Confirmation phrase
            </Label>
            <Input
              id="confirm-phrase"
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRM_PHRASE}
              disabled={running}
              data-testid="purge-confirm-input"
              className={confirmText && confirmText !== CONFIRM_PHRASE ? "border-rose-300" : ""}
            />
            <label className="flex items-center gap-2 text-xs text-slate-700 pt-1 cursor-pointer">
              <input
                type="checkbox"
                checked={resetIsLive}
                onChange={(e) => setResetIsLive(e.target.checked)}
                disabled={running}
                data-testid="purge-reset-live-checkbox"
              />
              Also reset <code className="bg-slate-100 px-1 rounded">accounts.is_live = false</code> (recommended)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={running}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={runPurge}
              disabled={confirmText !== CONFIRM_PHRASE || running}
              data-testid="purge-confirm-submit"
            >
              {running ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Purging…</>)
                : (<><Trash2 className="h-4 w-4 mr-1.5" /> Delete {preview?.total_deals ?? 0} deals</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RepairForecastsButton() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/repair/deal-forecasts");
      setResult(r.data);
      toast.success(`Repaired ${r.data.fixed} of ${r.data.scanned} deals`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Repair failed");
    } finally {
      setRunning(false);
    }
  };
  return (
    <div className="flex items-center gap-3">
      <Button
        onClick={run}
        disabled={running}
        data-testid="repair-forecasts-btn"
        className="bg-blue-600 hover:bg-blue-700 text-white"
      >
        {running
          ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Repairing…</>)
          : (<><Wrench className="h-4 w-4 mr-1.5" /> Repair now</>)}
      </Button>
      {result && (
        <span className="text-xs text-slate-600 font-mono" data-testid="repair-forecasts-result">
          scanned <b>{result.scanned}</b> · fixed <b>{result.fixed}</b>
        </span>
      )}
    </div>
  );
}

function PreviewStat({ label, value, testid }) {
  return (
    <div className="bg-white rounded border border-slate-200 p-3">
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-2xl font-heading font-bold text-slate-900 mt-0.5" data-testid={testid}>{value}</div>
    </div>
  );
}
